import { format } from "date-fns";

import Box from "@mui/material/Box";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import LoadingOrValue from "@covertree/ui/loading/LoadingOrValue";
import { DisplayPropertiesTable } from "@covertree/ui/content";
import ErrorContent from "@covertree/ui/content/ErrorContent";

import { formatAwsDateString } from "@covertree/utils";
import { Button, Modal } from "@covertree/ui";

import { ComplianceReview, Resident } from "@graphql/api";
import { getTllEffectiveDate } from "@utils";

import DetailsDrawerTop from "../../DetailsDrawerTop";

interface Props {
  canIssueTll: boolean;
  changeIssueTllModalOpened: (isOpened: boolean) => void;
  isError?: boolean;
  isLoadingResidentDetails?: boolean;
  isLoadingReviewDetails?: boolean;
  isIssueTllLoading: boolean;
  isIssueTllModalOpened: boolean;
  issueTll(): Promise<void>;
  onClose(): void;
  resident?: Resident;
  reviewDetails?: ComplianceReview;
}

export default function ResidentDetailsContent({
  canIssueTll,
  changeIssueTllModalOpened,
  isError,
  isLoadingResidentDetails,
  isLoadingReviewDetails,
  isIssueTllLoading,
  isIssueTllModalOpened,
  issueTll,
  onClose,
  resident,
  reviewDetails,
}: Props) {
  return (
    <>
      {canIssueTll && (
        <Modal
          open={isIssueTllModalOpened}
          disableClose={isIssueTllLoading}
          title="Confirm Adding BRL Coverage"
          onClose={() => changeIssueTllModalOpened(false)}
          maxWidth="sm">
          <Stack>
            <Typography>
              You&apos;re about to issue a BRL policy for this resident based on
              their opt-in request. The coverage will be effective on{" "}
              <strong>{format(getTllEffectiveDate(), "M/dd/yyyy")}</strong>.
            </Typography>
            <Typography mt={2}>Are you sure you want to proceed?</Typography>
            <Box p={4} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={issueTll}
                disabled={isIssueTllLoading}
                loading={isIssueTllLoading}>
                Confirm
              </Button>
              <Button
                variant="outlined"
                disabled={isIssueTllLoading}
                onClick={() => changeIssueTllModalOpened(false)}
                sx={{ ml: 2 }}>
                Cancel
              </Button>
            </Box>
          </Stack>
        </Modal>
      )}
      <Stack direction="column" pt={3} height="100%">
        <Box px={3}>
          <DetailsDrawerTop onClose={onClose} showResident={false} mb={4} />
          {!isError && (
            <Typography variant="h3" mb={4}>
              <LoadingOrValue
                loading={!resident?.firstName && isLoadingResidentDetails}
                value={`${resident?.firstName} ${resident?.lastName}${
                  resident?.residentId && `, ${resident.residentId}`
                }`}
                height={40}
                width={340}
              />
            </Typography>
          )}
        </Box>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            paddingTop: 0,
            flex: 1,
          }}>
          {!isError && (
            <Stack>
              <Box p={4}>
                <DisplayPropertiesTable
                  properties={[
                    {
                      label: "Review Start Date",
                      value: (
                        <LoadingOrValue
                          loading={
                            !reviewDetails?.startedAt && isLoadingReviewDetails
                          }
                          value={
                            reviewDetails?.startedAt
                              ? formatAwsDateString(reviewDetails.startedAt)
                              : ""
                          }
                          height={28}
                          width={200}
                        />
                      ),
                    },
                    {
                      label: "Review Start Reason",
                      value: (
                        <LoadingOrValue
                          loading={
                            !reviewDetails?.startReason &&
                            isLoadingReviewDetails
                          }
                          value={reviewDetails?.startReason ?? ""}
                          height={28}
                          width={200}
                        />
                      ),
                    },
                    ...(reviewDetails?.endedAt || reviewDetails?.expectedEndDate
                      ? [
                          {
                            label: "Review End Date",
                            value: (
                              <LoadingOrValue
                                loading={false}
                                value={formatAwsDateString(
                                  reviewDetails.endedAt ??
                                    reviewDetails.expectedEndDate ??
                                    undefined
                                )}
                                height={28}
                                width={200}
                              />
                            ),
                          },
                        ]
                      : []),
                    ...(reviewDetails?.endReason
                      ? [
                          {
                            label: "Review End Reason",
                            value: (
                              <LoadingOrValue
                                loading={false}
                                value={reviewDetails.endReason}
                                height={28}
                                width={200}
                              />
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </Box>
              {canIssueTll ? (
                <Box p={4} display="flex" justifyContent="flex-end">
                  <Button onClick={() => changeIssueTllModalOpened(true)}>
                    Add to BRL Coverage
                  </Button>
                </Box>
              ) : undefined}
            </Stack>
          )}
          {isError && (
            <ErrorContent>
              Oops! <br />
              Something went wrong while loading review details data. <br />
              Please try again later.
            </ErrorContent>
          )}
        </DialogContent>
      </Stack>
    </>
  );
}
